import type * as CSS from 'csstype';
import {EnrichedAstNode, AstView, AstPosition} from '../../types';
import {isEqualWithTolerance, groupNodeBuilder} from '../../helpers';
import {invertGapArray} from '../../helpers/GeometryHelpers';
import {checkGaps, getDistanceToParentFlexLabels, adjustGrouping} from '../GapMethods';

export const regroupAndReOrderChildren = (
    view: AstView,
    node: EnrichedAstNode,
    flexChildren: EnrichedAstNode[],
    flexDirection: CSS.Property.FlexDirection,
    createdBy: string
) => {
    if (node.type === 'Text') {
        return flexChildren;
    }

    const gaps = checkGaps(node.innerPosition, flexChildren);

    const justifyContentAxisGaps = flexDirection === 'row' ? gaps.x : gaps.y;

    const justifyContentAxis = adjustGrouping(justifyContentAxisGaps);

    const groupSections = invertGapArray(justifyContentAxis);

    if (groupSections.length === 1) {
        return flexChildren;
    }

    const output = groupSections.map((group, i) => {
        // TODO adjust to overflow same as regrouping step.
        const box =
            flexDirection === 'row'
                ? {
                      absoluteX: group.start,
                      absoluteY: node.innerPosition.absoluteY - 1000,
                      width: group.end - group.start,
                      height: node.innerPosition.height + 1000,
                  }
                : {
                      absoluteX: node.innerPosition.absoluteX - 1000,
                      absoluteY: group.start,
                      width: node.innerPosition.width + 1000,
                      height: group.end - group.start,
                  };

        const childrenInGroup = findNodesIn(
            box,
            flexChildren,
            flexDirection,
            i === 0 ? 'start' : i === groupSections.length - 1 ? 'end' : 'middle'
        );
        if (childrenInGroup.length === 0) {
            return undefined;
        } else if (childrenInGroup.length === 1) {
            return childrenInGroup[0];
        } else {
            const newGroup = groupNodeBuilder(childrenInGroup, view, createdBy, true);
            return newGroup;
        }
    });
    return output.filter((item) => item !== undefined) as EnrichedAstNode[];
};

export const verifyNoChildGroupingIsNeeded = (
    view: AstView,
    node: EnrichedAstNode,
    flexChildren: EnrichedAstNode[],
    flexDirection: CSS.Property.FlexDirection
) => {
    const gaps = checkGaps(node.innerPosition, flexChildren);

    const justifyContentAxis = flexDirection === 'row' ? gaps.x : gaps.y;

    const groupSections = invertGapArray(justifyContentAxis);

    groupSections.forEach((group, i) => {
        const box =
            flexDirection === 'row'
                ? {
                      absoluteX: group.start,
                      absoluteY: node.innerPosition.absoluteY,
                      width: group.end - group.start,
                      height: node.innerPosition.height,
                  }
                : {
                      absoluteX: node.innerPosition.absoluteX,
                      absoluteY: group.start,
                      width: node.innerPosition.width,
                      height: group.end - group.start,
                  };

        const childrenInGroup = findNodesIn(
            box,
            flexChildren,
            flexDirection,
            i === 0 ? 'start' : i === groupSections.length - 1 ? 'end' : 'middle'
        );
        if (childrenInGroup.length === 0) {
            throw Error('Bad grouping');
        } else if (childrenInGroup.length < 1) {
            throw Error('Bad grouping');
        }
    });
};

export function findNodesIn(
    position: AstPosition,
    nodes: EnrichedAstNode[],
    flexDirection: CSS.Property.FlexDirection,
    edgeCase: 'start' | 'end' | 'middle'
): EnrichedAstNode[] {
    // finds nodes that are within position box
    // if flexDirection === 'row'
    // check if node. absolute position.x is within position.x

    const newResults = nodes.filter((node) => {
        let startInside = false;
        let endInside = false;

        if (flexDirection === 'row') {
            startInside = node.position.absoluteX >= position.absoluteX;
            endInside = node.position.absoluteX < position.absoluteX + position.width;
        } else {
            startInside = node.position.absoluteY >= position.absoluteY;
            endInside = node.position.absoluteY < position.absoluteY + position.height;
        }

        if (edgeCase === 'start') {
            return endInside;
        } else if (edgeCase === 'end') {
            return startInside;
        } else if (edgeCase === 'middle') {
            return startInside && endInside;
        }
        return false;
    });
    return newResults;
}

export const addAlignAndMarginToChildren = (
    node: EnrichedAstNode,
    flexChildren: EnrichedAstNode[],
    flexDirection: CSS.Property.FlexDirection,
    addChildMargins: boolean
) => {
    // if parent is hug on secondary axis.
    // child needs provide width/height
    // tallest / widest child. Add margin to that one.

    const parentSecondaryAxisSpacing =
        flexDirection === 'column'
            ? node.calculatedLayoutAbstraction?.widthSpacingStrategy
            : node.calculatedLayoutAbstraction?.heightSpacingStrategy;

    const flexGapSize = node.innerPosition.gap;

    const marginPropertyJustifyAxis = flexDirection === 'row' ? 'marginRight' : 'marginBottom';

    const marginPropertyAlignAxis = flexDirection === 'row' ? 'marginBottom' : 'marginRight';

    const marginPropertyAlignAxisAlt = flexDirection === 'row' ? 'marginTop' : 'marginLeft';

    flexChildren.forEach((child, i) => {
        const {alignEdgeTopLeft, alignEdgeBottomRight} = getDistanceToParentFlexLabels(
            node,
            child,
            node.calculatedLayoutAbstraction.flexDirection
        );

        const childAlignSpacing =
            node.calculatedLayoutAbstraction.flexDirection === 'column'
                ? child.calculatedLayoutAbstraction.widthSpacingStrategy
                : child.calculatedLayoutAbstraction.heightSpacingStrategy;
        // adds spacing margin. we use this when flex gap is not possible
        if (i !== flexChildren.length - 1 && addChildMargins) {
            if (!child.mandatoryProperties.includes(marginPropertyJustifyAxis)) {
                child.mandatoryProperties.push(marginPropertyJustifyAxis);
            }
            const nextChild = flexChildren[i + 1];
            const bottomGap =
                nextChild.position.absoluteY -
                nextChild.position.marginTop -
                (child.position.absoluteY + child.position.height); // TODO account for margin here.
            const rightGap =
                nextChild.position.absoluteX -
                nextChild.position.marginLeft -
                (child.position.absoluteX + child.position.width);
            setMarginHelper(child, marginPropertyJustifyAxis, flexGapSize);
            const marginToAdd = (node.styles.flexDirection === 'row' ? rightGap : bottomGap) - flexGapSize;
            child.position[marginPropertyJustifyAxis] = marginToAdd;
        }

        const childAlignment =
            child.calculatedLayoutAbstraction?.alignSelf ||
            calculateAlignProperty(node, node.styles.flexDirection === 'row' ? 'row' : 'column', child);

        // add spacing properties here.

        if (childAlignment === 'flex-start') {
            setMarginHelper(child, marginPropertyAlignAxisAlt, alignEdgeTopLeft);
        } else if (childAlignment === 'flex-end') {
            setMarginHelper(child, marginPropertyAlignAxis, alignEdgeBottomRight);
        }
        if (childAlignSpacing === 'stretch') {
            setMarginHelper(child, marginPropertyAlignAxisAlt, alignEdgeTopLeft);
            setMarginHelper(child, marginPropertyAlignAxis, alignEdgeBottomRight);
        }

        child.styles.alignSelf = childAlignment;
        if (!child.mandatoryProperties.includes('alignSelf')) {
            child.mandatoryProperties.push('alignSelf');
        }
        child.styles = {
            ...child.styles,
        };
    });

    if (parentSecondaryAxisSpacing === 'hug' && flexChildren.length > 0) {
        const childDims = flexChildren.map((child) =>
            flexDirection === 'row' ? child.position.height : child.position.width
        );
        const tallestChild = Math.max(...childDims);
        const tallestChildIndex = childDims.indexOf(tallestChild);
        const tallestChildNode = flexChildren[tallestChildIndex];

        const {alignEdgeTopLeft, alignEdgeBottomRight} = getDistanceToParentFlexLabels(
            node,
            tallestChildNode,
            node.styles.flexDirection as 'row' | 'column'
        );

        setMarginHelper(tallestChildNode, marginPropertyAlignAxis, alignEdgeBottomRight);
        setMarginHelper(tallestChildNode, marginPropertyAlignAxisAlt, alignEdgeTopLeft);
    }
};

function setMarginHelper(
    node: EnrichedAstNode,
    margin: 'marginLeft' | 'marginRight' | 'marginTop' | 'marginBottom',
    amount: number
) {
    if (node.calculatedLayoutAbstraction?.userMarginSet && node.calculatedLayoutAbstraction.userMarginSet[margin]) {
        return;
    }
    if (!node.mandatoryProperties.includes(margin)) {
        node.mandatoryProperties.push(margin);
    }
    node.position[margin] = amount;
}

const FLEX_START_BIAS = 10;
export function calculateAlignProperty(
    parent: EnrichedAstNode,
    flexDirection: 'row' | 'column',
    child: EnrichedAstNode
): 'flex-start' | 'flex-end' | 'center' {
    const {alignEdgeTopLeft, alignEdgeBottomRight} = getDistanceToParentFlexLabels(parent, child, flexDirection, true);

    if (isEqualWithTolerance(alignEdgeTopLeft, alignEdgeBottomRight, 0.1, 'percent')) {
        return 'center';
    } else if (alignEdgeTopLeft > alignEdgeBottomRight + FLEX_START_BIAS) {
        return 'flex-end';
    } else {
        return 'flex-start';
    }
}
